/**
 * Mixin para las vistas de listado paginado contra el servidor.
 *
 * El bloque `options` + `getParamsPagination` + `loading`/`finally` estaba
 * duplicado en 29 vistas y ya había divergido: `totalItem` contra
 * `totalElements`, `itemsPerPage` sin default, el sort que nunca llegaba al
 * servidor, doble request al buscar y filtros que no reseteaban la página.
 *
 * Este mixin centraliza esa parte. La vista declara **qué** quiere pedir
 * (servicio, filtros, qué campo guarda las filas) y el mixin se ocupa de
 * **cómo** (paginación, loading, params, errores, sort).
 *
 * ## Uso
 *
 * ```js
 * import serverTableMixin from '@/mixins/serverTableMixin';
 *
 * export default {
 *     mixins: [serverTableMixin],
 *     data() {
 *         return {
 *             empresas: [],          // las filas que pide la vista
 *         };
 *     },
 *     computed: {
 *         // Los filtros van en un computed: así el mixin los vigila
 *         // automáticamente y resetea la página cuando cambian.
 *         tableFilters() {
 *             return {
 *                 deleted: this.showClientDeleted
 *             };
 *         }
 *     },
 *     methods: {
 *         // Único método OBLIGATORIO: qué servicio se llama.
 *         fetchTable(params) {
 *             return EmpresaDataService.table(params);
 *         },
 *         // Opcional: dónde se guardan las filas y el total.
 *         applyRows({ rows, total }) {
 *             this.empresas = rows;
 *             this.totalItems = total;
 *         }
 *     }
 * }
 * ```
 *
 * En la plantilla:
 *
 * ```html
 * <v-data-table :items="empresas" :options.sync="options"
 *               :loading="loading" :server-items-length="totalItems">
 * ```
 *
 * ## Filtros dinámicos
 *
 * `tableFilters` es un computed que declara la vista. El mixin lo vigila en
 * profundidad y, cuando cambia, **resetea la página a 1** y vuelve a
 * consultar. Es el comportamiento que se perdía en varias vistas (filtrar en
 * la página 3 dejaba al usuario en una página vacía).
 *
 * Los filtros se mandan al backend **planos**, junto a `page`, `itemsPerPage`
 * y `search`. El backend los recibe con un `Map<String, Object> filters` (que
 * ya existe en varios controllers) y, cuando pase a `Specification`, cada
 * clave se traduce en un predicado.
 */

import { getParamsPagination, normalizePage } from '@/utils/pagination';
import { extractMessage } from '@/helpers/errorHelper';

const serverTableMixin = {
    data() {
        return {
            // Estado de paginación y orden. Vuetify 2 escribe aquí a través
            // de `:options.sync`, y lee `sortBy`/`sortDesc` como arrays
            // paralelos (multi-sort nativo).
            options: {
                page: 1,
                itemsPerPage: 25,
                sortBy: [],
                sortDesc: []
            },
            loading: false,
            // Total de filas que reporta el servidor (no las que hay en la
            // página actual).
            totalItems: 0,
            // Texto de búsqueda de la vista.
            search: '',
            // Milisegundos de espera tras la última tecla antes de buscar
            // solo. 0 = apagado: la vista busca únicamente con `doSearch()`
            // (Enter o botón). La vista lo activa declarando su propio
            // valor en `data()`, p. ej. `searchDebounceMs: 500`.
            searchDebounceMs: 0
        };
    },

    created() {
        // No reactivos a propósito: solo sirven para no buscar dos veces lo
        // mismo (Enter + debounce).
        this._searchTimer = null;
        this._lastSearched = this.searchTerm;
    },

    beforeDestroy() {
        clearTimeout(this._searchTimer);
    },

    watch: {
        // Página, tamaño y sort se vigilan juntos, por contenido.
        // Vuetify emite `update:options` al montar y `:options.sync` reemplaza
        // `options` por un objeto nuevo con `sortBy`/`sortDesc` nuevos
        // (`[]` de nuevo). Vigilar cada campo por separado disparaba una
        // consulta por cada referencia nueva, aunque nada hubiera cambiado,
        // y un clic de sort (que cambia `sortBy` y `sortDesc` a la vez)
        // consultaba dos veces. Con una clave por contenido, cada cambio real
        // consulta una sola vez.
        tableQueryKey() {
            this.fetchTableData();
        },

        // Búsqueda automática tras una pausa al teclear (solo si la vista
        // activó `searchDebounceMs`).
        searchTerm() {
            if (!this.searchDebounceMs) return;
            clearTimeout(this._searchTimer);
            this._searchTimer = setTimeout(() => {
                // Si el usuario ya buscó con Enter, no repetir.
                if (this.searchTerm !== this._lastSearched) this.doSearch();
            }, this.searchDebounceMs);
        },

        // Un filtro cambió -> volver a la primera página y consultar.
        // Sin esto, filtrar en la página 3 deja al usuario en una página
        // vacía.
        //
        // `tableFilters` es un computed que declara la vista (ver
        // `buildTableParams`). Al ser un computed, sus fuentes reactivas
        // (un rango de fechas, un checkbox) ya lo reactivan.
        tableFilters: {
            deep: true,
            handler() {
                this.resetPage();
            }
        }
    },
    computed: {
        /**
         * Clave por contenido de página, tamaño y sort de `options`.
         *
         * Usa `options` y no `tableOptions`: en `pagedTableMixin` la página
         * vive en `page` y `initialize()` ya consulta por su cuenta; si
         * esta clave también la vigilara, cada cambio consultaría dos veces.
         *
         * @returns {string}
         */
        tableQueryKey() {
            const { page, itemsPerPage, sortBy, sortDesc } = this.options;
            return JSON.stringify([page, itemsPerPage, sortBy, sortDesc]);
        },

        /**
         * La paginación que se manda al backend.
         *
         * Por defecto es el `options` del mixin (el `v-data-table` escribe
         * ahí la página, el tamaño y el sort). La variante
         * `pagedTableMixin` lo reemplaza por su propio `page` e
         * `itemsPerPage`.
         *
         * @returns {object}
         */
        tableOptions() {
            return this.options;
        },

        /**
         * Los filtros dinámicos de la vista.
         *
         * Por defecto no hay ninguno. Las vistas que filtran declaran su
         * propio computed con el mismo nombre:
         *
         * ```js
         * computed: {
         *     tableFilters() {
         *         return {
         *             startDate: this.datesRange.start,
         *             endDate: this.datesRange.end
         *         };
         *     }
         * }
         * ```
         *
         * Como es un computed, el watcher del mixin se reactiva solo
         * cuando cambia cualquiera de sus fuentes.
         *
         * @returns {object}
         */
        tableFilters() {
            return {};
        },

        /**
         * El texto de búsqueda que se manda al backend.
         *
         * Por defecto es `this.search`. Las vistas que ya usaban otro
         * nombre para su campo de búsqueda (por ejemplo `searchContacto`)
         * no tienen que renombrarlo: basta con que declaren un computed
         * `searchTerm` con ese valor.
         *
         * @returns {string}
         */
        searchTerm() {
            return this.search;
        }
    },

    methods: {
        /**
         * Método OBLIGATORIO que implementa la vista: qué servicio se llama.
         * Debe devolver la promesa del servicio.
         *
         * El mixin NO define un valor por defecto a propósito. Si lo definiera,
         * Vue lo fusionaría en `$options.methods` y no se podría distinguir
         * "la vista lo implementó" de "es el valor por defecto". Al no
         * declararlo, `$options.methods.fetchTable` solo existe si la vista
         * lo declara, y `hasFetchTable()` puede avisar con un mensaje claro.
         *
         * @param {object} params page, itemsPerPage, search, sort y los filtros
         * @returns {Promise}
         */

        /**
         * Dónde se guardan las filas. Por defecto usa `tableRows`, que es el
         * nombre que declara la vista si no sobreescribe este método.
         *
         * @param {{ rows: Array, total: number }} page
         */
        applyRows({ rows, total }) {
            this.tableRows = rows;
            this.totalItems = total;
        },

        /**
         * Construye los params que se mandan al backend.
         *
         * La paginación sale de `tableOptions`, que por defecto es el
         * `options` del mixin. La variante `pagedTableMixin` lo cambia para
         * usar su propio `page` e `itemsPerPage`.
         *
         * @returns {object}
         */
        buildTableParams() {
            return getParamsPagination({
                options: this.tableOptions,
                search: this.searchTerm,
                params: this.tableFilters || {}
            });
        },

        /**
         * ¿La vista implementó `fetchTable`?
         *
         * No se puede comparar `this.fetchTable` con el método del mixin:
         * Vue enlaza los métodos a la instancia, así que `this.fetchTable`
         * es SIEMPRE una función nueva y nunca igual a la del mixin.
         *
         * Lo que sí es fiable es mirar las opciones del propio componente:
         * como el mixin no declara `fetchTable`, `$options.methods` solo lo
         * contiene si la vista lo declara.
         *
         * @returns {boolean}
         */
        hasFetchTable() {
            const own = this.$options.methods || {};
            return typeof own.fetchTable === 'function';
        },

        /**
         * Consulta los datos y actualiza el estado de la vista.
         * @returns {Promise}
         */
        async fetchTableData() {
            // La comprobación va FUERA del try: no implementar `fetchTable`
            // es un error de programación, no un fallo de la consulta. Si
            // estuviera dentro, el error se mostraría al usuario como si
            // el servidor hubiera fallado, y se perdería la traza.
            if (!this.hasFetchTable()) {
                throw new Error(
                    'serverTableMixin: la vista debe implementar fetchTable(params)'
                );
            }

            this.loading = true;
            try {
                const params = this.buildTableParams();
                const response = await this.fetchTable(params);
                const { rows, total, totalPages, currentPage } = normalizePage(
                    response && response.data
                );

                this.applyRows({ rows, total, totalPages, currentPage });
                this.totalItems = total;
                this.onTableLoaded({ rows, total, totalPages, currentPage });
            } catch (error) {
                this.onTableError(error);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Vuelve a la primera página. Se llama cuando cambia un filtro.
         * Si ya estamos en la 1ª, no hace falta consultar dos veces: el
         * watcher de `page` no se dispara y por eso se fuerza la consulta.
         */
        resetPage() {
            if (this.options.page === 1) {
                this.fetchTableData();
                return;
            }
            this.options.page = 1;
        },

        /**
         * Recarga la tabla conservando la página actual. Es lo que usa el
         * botón de refresco.
         */
        reload() {
            return this.fetchTableData();
        },

        /**
         * Búsqueda: resetea a la 1ª página y consulta.
         */
        doSearch() {
            clearTimeout(this._searchTimer);
            this._lastSearched = this.searchTerm;
            this.resetPage();
        },

        /**
         * Hook: se llama después de aplicar las filas.
         *
         * Por defecto no hace nada, y es a propósito: `options.page` es la
         * fuente de verdad de en qué página está el usuario. El servidor
         * devuelve `currentPage` en base 0 (la misma base en la que se lo
         * pidió), así que sincronizarlo aquí dejaría al usuario siempre en
         * la primera página: cada consulta lo devolvería al inicio.
         *
         * @param {object} page
         */
        // eslint-disable-next-line no-unused-vars
        onTableLoaded(page) {},

        /**
         * Hook: se llama si la consulta falla. Por defecto muestra el error.
         * @param {Error} error
         */
        onTableError(error) {
            const message = extractMessage(error);
            if (this.$swal) {
                this.$swal.fire({
                    position: 'top-end',
                    icon: 'error',
                    title: 'Error al obtener los datos',
                    text: message,
                    showConfirmButton: false,
                    timer: 2500
                });
            }
        }
    }
};

export default serverTableMixin;

/**
 * Variante para las vistas que usan el componente `PaginationNumber` en vez
 * del footer de Vuetify.
 *
 * La diferencia está en cómo se guarda la página: estas vistas la tienen en
 * `data()` (`page`, `itemsPerPage`, `totalPages`, `totalElements`) porque
 * `PaginationNumber` se enlaza a ellas con `.sync`, y no en `options`.
 *
 * `PaginationNumber` llama a `initialize({ page, itemsPerPage })`, así que
 * este mixin expone ese método con la misma firma.
 *
 * ```js
 * import { pagedTableMixin } from '@/mixins/serverTableMixin';
 *
 * export default {
 *     mixins: [pagedTableMixin],
 *     // ...
 * };
 * ```
 */
const pagedTableMixin = {
    mixins: [serverTableMixin],

    data() {
        return {
            // `PaginationNumber` se enlaza a estas con `.sync`, así que
            // tienen que vivir en la vista, no en el mixin.
            page: 1,
            totalPages: 0,
            itemsPerPage: 25,
            totalElements: 0
        };
    },

    computed: {
        // El mixin base manda `options` al backend; aquí la página vive en
        // `page` e `itemsPerPage`.
        //
        // El sort sí se conserva: `options` lo escribe el `v-data-table` y
        // `getParamsPagination` lo serializa a `sort=campo:asc,campo2:desc`.
        // Sin esto, una vista con `PaginationNumber` ordenaba en el cliente
        // (o de plano no ordenaba) y el backend nunca recibía el sort.
        tableOptions() {
            return {
                page: this.page,
                itemsPerPage: this.itemsPerPage,
                sortBy: this.options.sortBy,
                sortDesc: this.options.sortDesc
            };
        }
    },

    methods: {
        /**
         * `PaginationNumber` (y el botón de refresco) llaman a este método
         * con la página y el tamaño que quieren.
         *
         * @param {{ page: number, itemsPerPage: number }} options
         */
        initialize(options = {}) {
            const page = options.page || this.page || 1;
            const itemsPerPage = options.itemsPerPage || this.itemsPerPage || 25;

            this.page = page;
            this.itemsPerPage = itemsPerPage;

            return this.fetchTableData();
        },

        /**
         * En esta variante la página se guarda en `page`, no en
         * `options.page`.
         */
        resetPage() {
            if (this.page === 1) {
                this.fetchTableData();
                return;
            }
            this.page = 1;
        }
    }
};

export { pagedTableMixin };
